import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { initTelegramWebApp } from '@/lib/telegram'
import { applyThemePreference } from '@/lib/theme'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { AccessGate } from '@/components/AccessGate'
import { PageBoundary } from '@/components/PageBoundary'
import { ErrorState } from '@/components/ErrorState'
import { Spinner } from '@/components/Spinner'
import { BottomNav } from '@/components/BottomNav'
import { AppHeader } from '@/components/AppHeader'
import { HomePage } from '@/pages/HomePage'
import { PicksPage } from '@/pages/PicksPage'
import { ParlaysPage } from '@/pages/ParlaysPage'
import { MorePage } from '@/pages/MorePage'
import { SubscriptionPage } from '@/pages/SubscriptionPage'
import { RenewPlanPage } from '@/pages/RenewPlanPage'
import { MyBankrollPage } from '@/pages/MyBankrollPage'
import { HelpPage } from '@/pages/HelpPage'
import { ViewSettingsProvider } from '@/hooks/useViewSettings'
import { HistoryPage } from '@/pages/HistoryPage'
import { FavoritesPage } from '@/pages/FavoritesPage'
import { ChallengePage } from '@/pages/ChallengePage'
import { QuinielaPage } from '@/pages/QuinielaPage'
import { MyEvolutionPage } from '@/pages/MyEvolutionPage'
import { JALabPage } from '@/pages/JALabPage'
import { ParlayLabPage } from '@/pages/ParlayLabPage'
import { SGPLabPage } from '@/pages/SGPLabPage'
import { AdminHubPage } from '@/pages/AdminHubPage'
import { AdminUsersPage } from '@/pages/AdminUsersPage'
import { DesktopNav } from '@/components/DesktopNav'

function AuthenticatedApp() {
  const { status, user } = useAuth()
  const { isOnline, recheck } = useOnlineStatus()

  if (status === 'loading') return <Spinner />
  if (status !== 'active') return <AccessGate status={status} />

  return (
    <>
      <AppHeader />
      <div className="md:flex md:items-start">
        <DesktopNav />
        <div className="md:flex-1 md:min-w-0">
          {!isOnline ? (
            <div className="p-4">
              <ErrorState title="Sin conexión" body="No hay conexión a Internet." onRetry={recheck} />
            </div>
          ) : (
            <Routes>
              <Route path="/" element={<PageBoundary page="Home"><HomePage /></PageBoundary>} />
              <Route path="/picks" element={<PageBoundary page="Picks"><PicksPage /></PageBoundary>} />
              <Route path="/parlays" element={<PageBoundary page="Parlays"><ParlaysPage /></PageBoundary>} />
              {/* P0.23 (Sección 2): Reto y Banca promovidos a nivel superior -- mockup maestro. */}
              <Route path="/quiniela" element={<PageBoundary page="Quiniela"><QuinielaPage /></PageBoundary>} />
              <Route path="/reto" element={<PageBoundary page="Reto"><ChallengePage /></PageBoundary>} />
              <Route path="/banca" element={<PageBoundary page="Banca"><MyBankrollPage /></PageBoundary>} />
              {/* P0.56 (Sección 16 del encargo): JA Lab foundation/discovery -- visible para
                  los 3 planes, la propia página resuelve bloqueado/foundation según plan. */}
              <Route path="/ja-lab" element={<PageBoundary page="JALab"><JALabPage /></PageBoundary>} />
              {/* Sprint "Parlay V2 BLACK: Personalized Parlay Lab" (05/09/2026) -- mismo
                  patrón que JA Lab: visible para los 3 planes, la propia página resuelve
                  bloqueado/foundation según plan (Sección 7 del encargo: BLACK/ADMIN). */}
              <Route path="/parlay-lab" element={<PageBoundary page="ParlayLab"><ParlayLabPage /></PageBoundary>} />
              {/* Sprint "SGP V2 BLACK: Personalized SGP Lab" (05/09/2026) -- mismo patrón
                  que Parlay Lab: visible para los 3 planes, la propia página resuelve
                  bloqueado/foundation según plan (Sección 1 del encargo: BLACK/ADMIN). */}
              <Route path="/sgp-lab" element={<PageBoundary page="SGPLab"><SGPLabPage /></PageBoundary>} />
              <Route path="/mas" element={<PageBoundary page="Mas"><MorePage /></PageBoundary>} />
              <Route path="/mas/suscripcion" element={<PageBoundary page="Mas"><SubscriptionPage /></PageBoundary>} />
              <Route path="/mas/renovar" element={<PageBoundary page="Mas"><RenewPlanPage /></PageBoundary>} />
              <Route path="/mas/ayuda" element={<PageBoundary page="Mas"><HelpPage /></PageBoundary>} />
              {/* P0.53 (29/08/2026, Sección 11 del encargo): Historial unificado -- "Recomendaciones"
                  (oficial) + "Mis apuestas" (personal, `PersonalBet`) en UNA pantalla con 2 pestañas,
                  nunca mezclando sus fuentes de datos. Reemplaza la vieja pantalla solo-oficial. */}
              <Route path="/mas/historial" element={<PageBoundary page="Mas"><HistoryPage /></PageBoundary>} />
              <Route path="/mas/favoritos" element={<PageBoundary page="Mas"><FavoritesPage /></PageBoundary>} />
              {/* P0.53 (Sección 19/21 del encargo): "Mi Evolución" (PRO/BLACK) -- reemplaza al
                  Comparador de Estrategias como pantalla aislada; el Comparador oficial ahora
                  vive aquí dentro, como una pestaña, claramente etiquetado como rendimiento
                  OFICIAL (nunca personal). */}
              <Route path="/mas/evolucion" element={<PageBoundary page="Mas"><MyEvolutionPage /></PageBoundary>} />
              {/* P0.53 (Sección 7 del encargo): Match Lab deja de ser un destino visible/principal
                  del Client Web -- la ruta se conserva solo para no romper un enlace ya
                  compartido/guardado, pero ya no aparece en ningún menú de cliente. */}
              {/* FASE 7 -- JA Lab V2 (12/09/2026): "Match Lab" (clasificación binaria
                  SUPPORTED/LIMITED por competición, huérfana -- ningún nav link apuntaba
                  aquí) queda sustituida por la navegación real País/Europa → Competición →
                  Partido dentro de JA Lab (ver `JALabPage.tsx::CompetitionsBrowser`). Se
                  conserva la ruta como redirect (nunca un 404 para un enlace externo/
                  marcador ya guardado). */}
              <Route path="/mas/match-lab" element={<Navigate to="/ja-lab" replace />} />
              {/* Sprint "Corrección Final Product Experience V2" (10/09/2026, Secciones 7/10
                  del encargo): "Administración" -- SOLO ADMIN. Defensa en profundidad en el
                  cliente (un no-ADMIN que navegue a mano cae a Inicio, nunca ve la pantalla),
                  pero la barrera REAL es `require_admin_role` en cada endpoint que estas
                  páginas consumen (403 real, probado -- ver reporte final). */}
              <Route path="/mas/admin" element={<PageBoundary page="Mas">{user?.role === 'ADMIN' ? <AdminHubPage /> : <Navigate to="/" replace />}</PageBoundary>} />
              <Route path="/mas/admin/usuarios" element={<PageBoundary page="Mas">{user?.role === 'ADMIN' ? <AdminUsersPage /> : <Navigate to="/" replace />}</PageBoundary>} />
              {/* Rutas antiguas conservadas -- no en menú, accesibles por URL directa (nunca
                  rompemos un enlace ya compartido/guardado). */}
              <Route path="/mas/mi-banca" element={<Navigate to="/banca" replace />} />
              <Route path="/mas/challenge" element={<Navigate to="/reto" replace />} />
              <Route path="/mas/comparador" element={<Navigate to="/mas/evolucion" replace />} />
              {/* P0.53 (BLOCK A, Sección 5 del encargo): las 4 superficies administrativas
                  (Gestión de usuarios, Auditoría de pagos, Banca del Sistema, Centro de Control
                  de IA) se retiran por completo de Client Web -- su equivalente ya existe en el
                  Dashboard administrativo (`frontend/`). Un enlace antiguo redirige a Inicio en
                  vez de un 404 críptico; nunca hay una segunda copia de la herramienta admin aquí. */}
              <Route path="/mas/usuarios" element={<Navigate to="/" replace />} />
              <Route path="/mas/pagos" element={<Navigate to="/" replace />} />
              <Route path="/mas/bankroll" element={<Navigate to="/" replace />} />
              <Route path="/mas/sistema" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </div>
      </div>
      <BottomNav />
    </>
  )
}

export function App() {
  useEffect(() => {
    applyThemePreference()
    initTelegramWebApp()
  }, [])

  return (
    <PageBoundary page="App">
      <AuthProvider>
        <ViewSettingsProvider>
          <AuthenticatedApp />
        </ViewSettingsProvider>
      </AuthProvider>
    </PageBoundary>
  )
}
